import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import type { ReferralLead } from "@/hooks/useReferralLeads";

export interface PaginatedReferralLeadsParams {
  page: number;
  perPage: number;
  /** Filter to a single status; omit or "all" for every status. */
  status?: string;
}

/** Server-side paginated + status-filtered referral leads for the Coordinator
 *  Outreach page — only the current page's rows are ever pulled into memory.
 *  Full-list aggregate views (Reports, Analytics) keep using useReferralLeads(). */
export function usePaginatedReferralLeads(params: PaginatedReferralLeadsParams) {
  const { user } = useAuth();
  // See useProfile.ts — depend on the stable user id, not the object
  // reference, which changes on every Supabase token refresh.
  const userId = user?.id;
  const [leads, setLeads] = useState<ReferralLead[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const requestId = useRef(0);

  const { page, perPage, status } = params;

  const fetchPage = useCallback(async () => {
    if (!userId) { setLeads([]); setTotalCount(0); setLoading(false); return; }
    const id = ++requestId.current;
    setLoading(true);

    let query = supabase.from("referral_leads").select("*", { count: "exact" });
    if (status && status !== "all") query = query.eq("status", status);
    query = query
      .order("created_at", { ascending: false })
      .range(page * perPage, page * perPage + perPage - 1) as any;

    const { data, count, error } = await query;
    if (id !== requestId.current) return;
    if (!error) {
      setLeads((data as ReferralLead[]) ?? []);
      setTotalCount(count ?? 0);
    }
    setLoading(false);
  }, [userId, page, perPage, status]);

  useEffect(() => { fetchPage(); }, [fetchPage]);

  return { leads, totalCount, loading, refetch: fetchPage };
}
